<template>
  <div>
    <v-row-wrapper
      v-for="(chunkedRow, chunkedRowIndex) in chunkedFields"
      :key="chunkedRowIndex"
    >
      <v-col-wrapper
        v-for="(field, fieldIndex) in chunkedRow"
        :key="getColumnKey(fieldIndex, field)"
        :cols="getFieldCols(field)"
        :class="field.colClass || ''"
        :style="field.colStyle || {}"
      >
        <div
          v-if="field && field.type === 'plain-text' && field.text"
          :class="field.class || ''"
          :style="field.style || {}"
        >
          {{ field.text }}
        </div>
        <!--If field type is not provided, it will be text field by default-->
        <div
          v-if="canShowField(field, 'text')"
          class="dblclick-wrapper"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <text-field
            v-model="modifiedFormData[field.key]"
            :field="field"
            :update-data="updateData"
            :edit-mode="edit"
            :create-mode="createMode"
            :remove-data-property-by-key="removeDataPropertyByKey"
            :replace-property-value-by-key="replacePropertyValueByKey"
            :update-validation-errors="updateValidationErrors"
            :validation-errors="validationErrors"
            :form-data="modifiedFormData"
            v-bind="field.props || {}"
            :readonly="shouldDisableField(field)"
            @clear-validation-errors="onClearValidationErrors(field)"
          />
        </div>

        <!--                  Autocomplete field-->
        <div
          v-else-if="canShowField(field, 'autocomplete')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <autocomplete-field
            v-model="modifiedFormData[field.key]"
            :field="field"
            :edit-mode="edit"
            :create-mode="createMode"
            :items="getAutocompleteItems(field)"
            :options="field && field.autocomplete_options ? field.autocomplete_options : {}"
            :validation-errors="validationErrors"
            :update-data="updateData"
            :form-data="modifiedFormData"
            :remove-data-property-by-key="removeDataPropertyByKey"
            :replace-property-value-by-key="replacePropertyValueByKey"
            :update-validation-errors="updateValidationErrors"
            :readonly="shouldDisableField(field)"
            v-bind="field.props || {}"
            @clear-validation-errors="onClearValidationErrors(field)"
          />
        </div>

        <!--        Textarea field-->
        <div
          v-else-if="canShowField(field, 'textarea')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <textarea-field
            v-model="modifiedFormData[field.key]"
            :config="field"
            :edit-mode="edit"
            :create-mode="createMode"
            :validation-errors="validationErrors"
            :remove-data-property-by-key="removeDataPropertyByKey"
            :replace-property-value-by-key="replacePropertyValueByKey"
            :readonly="shouldDisableField(field)"
            v-bind="field.props || {}"
            @clear-validation-errors="onClearValidationErrors(field)"
          />
        </div>

        <!--                  Combobox field-->
        <div
          v-else-if="canShowField(field, 'combobox')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <combobox-field
            v-model="modifiedFormData[field.key]"
            :options="field.autocomplete_options || {}"
            :validation-errors="validationErrors"
            :items="getAutocompleteItems(field)"
            :field="field"
            :edit-mode="edit"
            :create-mode="createMode"
            :readonly="shouldDisableField(field)"
            v-bind="field.props || {}"
            @clear-validation-errors="onClearValidationErrors(field)"
          />
        </div>

        <!--                  custom component-->
        <div
          v-else-if="canShowField(field, 'custom') && field.component"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <component
            :is="field.component"
            :config="field"
            :edit-mode="edit"
            :form-data="modifiedFormData"
            :create-mode="createMode"
            :validation-errors="validationErrors"
            :disabled="shouldDisableField(field)"
            v-bind="field.props || {}"
            @methods="onCustomComponentMethods($event, field)"
            @clear-validation-errors="onClearValidationErrors(field, $event)"
          />
        </div>

        <!--                  vehicles selection-->
        <div
          v-else-if="canShowField(field, 'vehicles-selection')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <satellite-tracking-vehicles-preview
            v-model="modifiedFormData[field.key]"
            :max-select="'maxSelect' in field ? field.maxSelect : 10"
            :disabled="shouldDisableField(field)"
            :edit-mode="edit"
            :field="field"
            :create-mode="createMode"
            :disable-open="shouldDisableField(field)"
            :title="$t('satellite-tracking/tracking-history.choose_vehicles')"
          />
        </div>

        <!--                  persons selection-->
        <div
          v-else-if="canShowField(field, 'persons-selection')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <person-picker
            v-model="modifiedFormData[field.key]"
            :max-select="field.maxSelect || 1"
            :label="field.label"
            :edit-mode="edit"
            :display-field-config="field"
            :create-mode="createMode"
            :disable-open="shouldDisableField(field)"
            :start-open="field.startOpen !== undefined ? field.startOpen : false"
          />
        </div>

        <!--        color field-->
        <div
          v-else-if="canShowField(field, 'color')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <!--    Carried above the picker like every other field's label, so the
                  control lines up with the inputs beside it.-->
          <div
            v-if="field && field.label"
            class="field-label"
          >
            {{ field.label }}
          </div>

          <v-menu
            offset-y
            max-width="300"
            :disabled="!edit && !createMode"
          >
            <template
              #activator="{ on }"
            >
              <div
                style="display: flex; flex-direction: column;"
                v-on="on"
              >
                <!--    A plain swatch rather than a button: the field's label sits
                        above it, and the square itself carries no wording.-->
                <div
                  class="color-field-swatch"
                  :style="{ backgroundColor: modifiedFormData[field.key] || '#9E9E9E' }"
                />
                <span
                  v-if="validationErrors[field.key] && validationErrors[field.key][0]"
                  style="color: #ff5252; font-size: 12px; line-height: 12px; padding-top: 5px; padding-left: 12px;"
                >
                  {{ validationErrors[field.key][0] }}
                </span>
              </div>
            </template>
            <v-color-picker
              :value="modifiedFormData[field.key]"
              :mode="field.mode ? field.mode : 'hexa'"
              hide-inputs
              show-swatches
              @update:color="event => $set(modifiedFormData, field.key, event.hex)"
            />
          </v-menu>
        </div>

        <!--                  Image field-->
        <div
          v-else-if="canShowField(field, 'image')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <div
            v-if="field && field.label"
            class="image-label"
          >
            {{ field.label }}
          </div>
          <image-field
            :src="getImageFieldSrc(field)"
            :width="field.width || undefined"
            :height="field.height || undefined"
            :contain="field.contain || false"
          />
        </div>

        <!--                  select field-->
        <div
          v-else-if="canShowField(field, 'select')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <select-field
            v-model="modifiedFormData[field.key]"
            :config="selectFieldConfig(field)"
            :disabled="shouldDisableField(field)"
            :edit-mode="edit"
            :create-mode="createMode"
            @clear-validation-errors="onClearValidationErrors(field)"
          />
        </div>

        <!--                  Checkbox field-->
        <div
          v-else-if="canShowField(field, 'checkbox')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <checkbox-field
            v-model="modifiedFormData[field.key]"
            :config="field"
            v-bind="field.props || {}"
            :disabled="shouldDisableField(field)"
          />
        </div>

        <!--                  Switch field-->
        <div
          v-else-if="canShowField(field, 'switch')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <switch-field
            v-model="modifiedFormData[field.key]"
            :config="field"
            v-bind="field.props || {}"
            :disabled="shouldDisableField(field)"
          />
        </div>

        <!--                  base switch field-->
        <div
          v-else-if="canShowField(field, 'base-switch')"
          style="width: fit-content;"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <base-switch-field
            v-model="modifiedFormData[field.key]"
            :config="field"
            :disabled="shouldDisableField(field)"
          />
        </div>

        <!--                  slider-->
        <div
          v-else-if="canShowField(field, 'slider')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <slider
            v-model="modifiedFormData[field.key]"
            :config="field"
            :disabled="shouldDisableField(field)"
            :readonly="shouldDisableField(field)"
          />
        </div>

        <!--                  range slider-->
        <div
          v-else-if="canShowField(field, 'range-slider')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <range-slider
            v-model="modifiedFormData[field.key]"
            :config="field"
            :disabled="shouldDisableField(field)"
            :readonly="shouldDisableField(field)"
          />
        </div>

        <!--        Dynamic content with buttons field-->
        <div
          v-else-if="canShowField(field, 'content-with-buttons')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <div
            :style="getContentWrapperStyle(field)"
          >
            <div
              style="display: flex; flex-direction: column;"
              :style="field && field.contentStyle && (field.contentStyle.minWidth || field.contentStyle.maxWidth || field.contentStyle.width) ? {} : { width: '100%' }"
            >
              <span
                class="label mb-1"
              >
                {{ field && field.label ? field.label : '' }}
              </span>
              <div
                :style="getDynamicContentStyle(field)"
                style="display: flex; justify-content: center; align-items: center;"
              >
                <template
                  v-if="field.contentType === 'image'"
                >
                  <v-img
                    :src="getContentImageSrc(field)"
                    width="120"
                    height="90"
                    style="flex: unset !important;"
                    contain
                  />
                </template>
              </div>
            </div>
            <template
              v-if="field && field.buttons && field.buttons.length"
            >
              <div
                :style="getContentButtonsWrapperStyle(field)"
                class="mt-6"
              >
                <div
                  :style="{ cursor: permissions && !permissions.canEdit ? 'not-allowed' : 'default' }"
                >
                  <template
                    v-for="(button, buttonIndex) in field.buttons"
                  >
                    <universal-button
                      v-if="getContentCardButtonVisibility(button)"
                      :key="buttonIndex"
                      :type="button && button.type ? button.type : 'base'"
                      :color="button && button.color ? button.color : ''"
                      :style="getContentButtonStyle(button)"
                      :prepend-icon="getContentButtonPrependIcon(button)"
                      :class="getContentButtonClasses(field)"
                      :label="getContentButtonLabel(button)"
                      :disabled="getInsideCardButtonDisabledValue(button)"
                      @click="onContentButtonClick(button)"
                    />
                  </template>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!--        Datetime picker-->
        <div
          v-else-if="canShowField(field, 'datetime-picker')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <v-menu
            v-model="openedDatePickers[field.key]"
            transition="slide-y-transition"
            nudge-top="530"
            offset-y
            min-width="auto"
            :disabled="shouldDisableField(field)"
            :close-on-content-click="false"
          >
            <template
              #activator="{}"
            >
              <text-field
                v-model="modifiedFormData[field.key]"
                :field="getDatetimeFieldConfig(field)"
                :form-data="modifiedFormData"
                :update-data="updateData"
                :edit-mode="edit"
                :create-mode="createMode"
                :remove-data-property-by-key="removeDataPropertyByKey"
                :replace-property-value-by-key="replacePropertyValueByKey"
                :update-validation-errors="updateValidationErrors"
                :validation-errors="validationErrors"
                :readonly="field.clickable ? true : shouldDisableField(field)"
                @append-click="openDatePicker(field)"
                @clear-validation-errors="onClearValidationErrors(field)"
              />
            </template>
            <datetime-picker
              v-if="openedDatePickers[field.key]"
              v-model="modifiedFormData[field.key]"
              :date-format="field && field.dateFormat ? field.dateFormat : null"
              :field="field"
              @close="closeDatePicker(field)"
              @dblclick:date="(event) => onDateDblClick(field, event)"
            />
          </v-menu>
        </div>

        <!--Files upload field-->
        <div
          v-else-if="canShowField(field, 'files_upload')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <span
            style="font-size: 14px; line-height: 20px; font-weight: 500; color: #111827; font-family: 'Satoshi', sans-serif;"
          >
            {{ field && field.label ? field.label : '' }}
          </span>
          <div
            class="files-upload-div mt-1"
          >
            <v-badge
              v-if="modifiedFormData[field.key] && modifiedFormData[field.key].length"
              :content="modifiedFormData[field.key] && modifiedFormData[field.key].length ? modifiedFormData[field.key].length.toString() : '0'"
              :color="'#3B82F6'"
              style="cursor: pointer;"
              overlap
              offset-x="13"
              offset-y="14"
            >
              <image-field
                :src="getFileFieldIcon(field)"
                :contain="field.contain || true"
                :height="field.height || 30"
                :width="field.width || 30"
                @onClick="onShowFilesPreview(field)"
              />
            </v-badge>
            <v-icon
              v-else
            >
              {{ getFileFieldIconEmpty(field) }}
            </v-icon>
            <universal-button
              type="base"
              text
              x-small
              :ripple="false"
              :elevation="0"
              :disabled="!edit && !createMode"
              class="no-background-hover ml-2"
              style="border: unset !important; color: black;"
              append-icon="mdi-plus"
              @click="triggerFileInput(field)"
            />
          </div>
          <input
            :ref="`files_input_${field.key}`"
            type="file"
            class="d-none"
            :accept="field && field.accept !== undefined ? field.accept : '*.*'"
            :multiple="field && field.multiple !== undefined ? field.multiple : true"
            enctype="multipart/form-data"
            @change="event => onFilesInput(event, field)"
          >
        </div>

        <!--Files preview field-->
        <div
          v-else-if="canShowField(field, 'files_preview')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <span
            style="font-size: 14px; line-height: 20px; font-weight: 500; color: #111827; font-family: 'Satoshi', sans-serif;"
          >
            {{ field && field.label ? field.label : '' }}
          </span>
          <div
            class="files-upload-div mt-1"
          >
            <v-badge
              :content="modifiedFormData[field.key].length.toString()"
              :color="'#3B82F6'"
              style="cursor: pointer;"
              overlap
              offset-x="13"
              offset-y="14"
            >
              <image-field
                :src="getFilesPreviewIconSrc(field)"
                :contain="field.contain || true"
                :height="field.height || 30"
                :width="field.width || 30"
                @onClick="onShowFilesPreview(field)"
              />
            </v-badge>
          </div>
        </div>

        <div
          v-else-if="canShowField(field, 'images-stack')"
          @dblclick="unlockFieldsOnDblClick(field)"
        >
          <manageable-image-stack
            v-model="modifiedFormData[field.key]"
            :config="field"
            :edit="edit"
            :create-mode="createMode"
            :data="modifiedFormData"
            :update-data="updateData"
            :enable-actions="edit || createMode"
            @images:uploaded="images => setImages(images, field.key)"
          />
        </div>
      </v-col-wrapper>
    </v-row-wrapper>
  </div>
</template>

<script>
import TextField from '@/global/components/fields/TextField.vue'
import AutocompleteField from '@/global/components/fields/AutocompleteField.vue'
import ComboboxField from '@/global/components/fields/ComboboxField.vue'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
// `this.$store`, not an import: importing the store here puts this component in
// an import cycle (the store eagerly evaluates every module .js through the
// module-directory-scanner's require.context, and those import the views that
// render this component), which leaves it registered as `undefined` on whichever
// parent happens to be evaluated first.
import { cloneDeep, forEach, isArray, isObject } from 'lodash'
import DatetimePicker from '@/global/components/fields/DatetimePicker.vue'
import TextareaField from '@/global/components/fields/TextareaField.vue'
import ImageField from '@/global/components/fields/ImageField.vue'
import CheckboxField from '@/global/components/fields/CheckboxField.vue'
import SwitchField from '@/global/components/fields/SwitchField.vue'
import ManageableImageStack from '@/global/components/fields/ImageStackField.vue'
import BaseSwitchField from '@/global/components/fields/BaseSwitchField.vue'
import Slider from '@/global/components/fields/Slider.vue'
import RangeSlider from '@/global/components/fields/RangeSlider.vue'
import SatelliteTrackingVehiclesPreview from '@/modules/satellite-tracking-module/components/SatelliteTrackingVehiclesPreview.vue'
import SelectField from '@/global/components/fields/SelectField.vue'
import PersonPicker from '@/modules/people-module/components/PersonPicker.vue'
import VRowWrapper from '@/global/components/vuetifyComponentsWrappers/VRowWrapper.vue'
import VColWrapper from '@/global/components/vuetifyComponentsWrappers/VColWrapper.vue'
import helperMixin from '@/global/mixins/helperMixin'

const MAX_COLUMNS = 12
const DEFAULT_COLUMNS_COUNT = 2
export default {
  name: 'FieldsRenderer',

  components: {
    VColWrapper,
    VRowWrapper,
    PersonPicker,
    SatelliteTrackingVehiclesPreview,
    RangeSlider,
    Slider,
    BaseSwitchField,
    SwitchField,
    SelectField,
    CheckboxField,
    ImageField,
    TextareaField,
    ManageableImageStack,
    DatetimePicker,
    ComboboxField,
    AutocompleteField,
    TextField,
    UniversalButton
  },

  mixins: [
    helperMixin
  ],

  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    },
    initialAutocompletes: {
      type: Object,
      default: () => ({})
    },
    edit: {
      type: Boolean,
      default: false
    },
    createMode: {
      type: Boolean,
      default: false
    },
    fieldsConfig: {
      type: Object,
      default: () => ({})
    },
    permissions: {
      type: Object,
      default: null
    },
    updateData: {
      type: Function,
      default: () => {}
    },
    updateValidationErrors: {
      type: Function,
      default: () => {}
    },
    removeDataPropertyByKey: {
      type: Function,
      default: () => {}
    },
    replacePropertyValueByKey: {
      type: Function,
      default: () => {}
    }
  },

  data () {
    return {
      modifiedFormData: {},
      openedDatePickers: []
    }
  },

  computed: {
    disabled () {
      return this.config?.disabled === true
    },

    columnsCount () {
      return this.config?.columnsCount || DEFAULT_COLUMNS_COUNT
    },

    fields () {
      return this.config?.fields
    },

    chunkedFields () {
      if (!this.fields?.length) {
        return []
      }

      const grouped = {}

      this.fields.forEach(field => {
        const { visible } = field || {}
        let visibleValue = visible
        if (typeof visible === 'function') {
          visibleValue = visible(this)
        }

        if (visibleValue) {
          if (!grouped[field.row]) {
            grouped[field.row] = []
          }
          grouped[field.row].push(field)
        }
      })

      // Ensure each row fills up to columnCount by adding placeholders if needed
      Object.keys(grouped).forEach(rowIndex => {
        grouped[rowIndex] = this.fillRowWithPlaceholders(grouped[rowIndex])
      })

      return Object.values(grouped)
    }
  },

  watch: {
    formData: {
      immediate: true,
      deep: true,
      handler (formData) {
        this.modifiedFormData = formData
      }
    }
  },

  methods: {
    onCustomComponentMethods (event, field) {
      const { methods } = field || {}
      methods?.({
        event
      })
    },

    setImages (images, key) {
      this.updateData({
        [key]: images
      })
    },

    getColumnKey (index, field) {
      const { key } = field || {}
      return `${index}-${key || ''}`
    },

    getInsideCardButtonDisabledValue (button) {
      const { disabled } = button || {}
      let disabledValue = disabled

      if (!button) {
        return true
      }

      if (this.edit) {
        return true
      }

      if (disabled && typeof disabled === 'function') {
        disabledValue = disabled(this)
      }

      return disabledValue !== undefined && disabledValue
    },

    // `field.clickable` opts a datetime-picker field into opening on a click
    // anywhere on the field body (not just the append icon) while disabling
    // manual typing. `forceEditStyle` keeps the usual green "editing" look
    // even though the field is always readonly, and `onClick` both opens the
    // picker and gives the field the pointer cursor (see TextField.vue).
    getDatetimeFieldConfig (field) {
      if (!field || !field.clickable || !this.edit) {
        return field
      }

      return {
        ...field,
        forceEditStyle: true,
        onClick: () => this.openDatePicker(field)
      }
    },

    openDatePicker (field) {
      const { key } = field || {}

      if (!key) {
        return
      }

      this.$set(this.openedDatePickers, key, true)
    },

    closeDatePicker (field) {
      const { key } = field || {}

      if (!key) {
        return
      }

      this.onClearValidationErrors(field)
      this.$set(this.openedDatePickers, key, false)
    },

    onClearValidationErrors (field, customKey = null) {
      const { key, validationPropertyKey, validationErrorIncludeKey } = field || {}
      const validationErrorKey = customKey || validationPropertyKey || validationErrorIncludeKey || key
      this.$emit('clear-validation-errors', validationErrorKey)
    },

    onDateDblClick (field, event) {
      const { closeOnDateDblClick = true, key } = field || {}
      const { applyDate } = event || {}

      // do not apply date on dbl click if
      // key is missing
      // or applyDate callback is not a function
      if (!key || typeof applyDate !== 'function') {
        return
      }

      // control applying date on dbl click by field
      if (closeOnDateDblClick) {
        applyDate()
      }
    },

    getFieldCols (field) {
      const colWidth = Math.floor(MAX_COLUMNS / this.columnsCount)
      const fieldColSpan = field.colSpan || 1

      return this.isMobile ? 12 : fieldColSpan * colWidth
    },

    fillRowWithPlaceholders (rowFields) {
      const filledRow = []
      let currentColumn = 1

      rowFields.forEach((field) => {
        // Calculate offset for any gap before the field's column position
        if (field.column > currentColumn) {
          const gapSize = field.column - currentColumn
          filledRow.push({
            isPlaceholder: true,
            colSpan: gapSize
          })
          currentColumn += gapSize
        }

        // Add the actual field with its specified colSpan
        filledRow.push(field)
        currentColumn += field.colSpan || 1

        // Check if we've reached or exceeded the column count for the row
        if (currentColumn >= this.columnsCount) {
          currentColumn = this.columnsCount // Keep it capped at columnsCount
        }
      })

      // Fill remaining space with placeholders if row doesn't reach `columnsCount`
      while (currentColumn < this.columnsCount) {
        filledRow.push({
          isPlaceholder: true,
          colSpan: 1
        })
        currentColumn += 1
      }

      return filledRow
    },

    canShowField (field, fieldType = null) {
      const { type, key, initialValue, visible } = field || {}
      let visibleValue = visible

      // Set initial field value
      if (initialValue && key && this.modifiedFormData[key] === undefined) {
        let initialValueModified = initialValue

        if (typeof initialValue === 'function') {
          initialValueModified = initialValue(this)
        }

        this.$set(this.modifiedFormData, key, initialValueModified)
      }

      if (visible && typeof visible === 'function') {
        visibleValue = visible(this)
      }

      return !!fieldType && typeof key === 'string' && key && (!type || type === fieldType) && visibleValue
    },

    // SelectField derives its `disabled`/`readonly` from its own config rather
    // than from the passed prop, so the shouldDisableField() prop has no effect
    // on it. When the tab is read-only (permissions.canEdit === false) inject
    // `disabled` into the config so a select cannot be changed in preview mode.
    selectFieldConfig (field) {
      if (this.permissions && this.permissions.canEdit === false) {
        return { ...field, disabled: true }
      }
      return field
    },

    shouldDisableField (field) {
      const { key, editable = true, unlock } = field || {}

      if (unlock) {
        // An `unlock` field is editable outside edit-mode, but when the tab is
        // read-only (permissions.canEdit === false) it must stay disabled too.
        return !!(this.permissions && this.permissions.canEdit === false)
      }

      if (typeof key !== 'string' || !key) {
        return false
      }

      const fieldConfig = this.fieldsConfig[key]

      // control field edit state by 'editable' field's property in create mode
      if (this.createMode) {
        return !editable
      }

      // disable field by setting false for 'editable' field property
      if (!editable) {
        return true
      }

      return fieldConfig && typeof fieldConfig.editable !== 'undefined' ? !fieldConfig.editable || !this.edit : !this.edit
    },

    canEditField (field) {
      const { key, editable, type, onClick } = field || {}

      if (typeof key !== 'string' || !key) {
        return false
      }

      const fieldConfig = this.fieldsConfig[key]

      // `editable: false` normally means "purely informational, don't let a
      // double-click on it unlock editing" - but datetime-pickers and fields
      // with their own `onClick` (eg. a "pick from dialog" field) are always
      // read-only inputs by design, not informational text, so a double-click
      // on them should still be able to unlock the row for editing.
      if (!editable && type !== 'datetime-picker' && typeof onClick !== 'function') {
        return false
      }

      return fieldConfig && typeof fieldConfig.editable !== 'undefined' ? fieldConfig.editable : true
    },

    unlockFieldsOnDblClick (field) {
      const { doubleClickEdit = true } = field || {}
      // do not enable editing if permissions exist and canEdit is false
      // or disabled property from config is false
      if (this.disabled || (this.permissions && !this.permissions.canEdit)) {
        return
      }

      // if field does not exist, allow editing
      if (!field) {
        this.$emit('unlock-fields-on-dbl-click')
        return
      }

      // block if not allowed per field or mode
      if (this.createMode || !this.canEditField(field) || !doubleClickEdit) {
        return
      }

      this.$emit('unlock-fields-on-dbl-click')
    },

    getFileFieldIcon (field) {
      const { subtype, src } = field || {}
      if (!subtype) {
        return
      }

      if (src && typeof src === 'string') {
        return src
      }
      else if (typeof src === 'function') {
        const srcVal = src()
        if (srcVal && typeof srcVal === 'string') {
          return srcVal
        }
      }

      switch (field.subtype) {
        case 'image':
          return '/img/icons/images_album_preview.png'
        case 'pdf':
          return '/img/icons/pdf_preview.png'
      }
    },

    getFilesPreviewIconSrc (field) {
      const { src } = field || {}
      let srcVal = src
      if (src && typeof src === 'string') {
        return srcVal
      }
      else if (typeof src === 'function') {
        srcVal = src()
      }

      return srcVal && typeof srcVal === 'string' ? srcVal : undefined
    },

    getFileFieldIconEmpty (field) {
      if (!field || !field.subtype) {
        return
      }

      switch (field.subtype) {
        case 'image':
          return 'mdi-camera'
        case 'pdf':
          return 'mdi-file-pdf-box'
      }
    },

    onShowFilesPreview (field) {
      const { key: fieldKey, click } = field
      // skip if field key is not provided
      if (!fieldKey) {
        return
      }

      // make a files copy to avoid changes on original object from outside
      const filesCopy = cloneDeep(this.modifiedFormData)
      if (typeof click === 'function') {
        click({
          data: filesCopy?.[fieldKey] || []
        })
      }
    },

    triggerFileInput (field) {
      if (!field || !field.key || (!this.edit && !this.createMode)) {
        return
      }
      this.$refs[`files_input_${field.key}`][0].click()
      this.$refs[`files_input_${field.key}`][0].value = ''
    },

    onFilesInput (event, field) {
      const uploadedFiles = event.target.files
      const { key: fieldKey } = field || {}
      // skip if field key is not provided
      if (!fieldKey) {
        return
      }

      if (this.modifiedFormData && !this.modifiedFormData[fieldKey]) {
        this.$set(this.modifiedFormData, fieldKey, [])
      }

      const isImage = field.subtype === 'image'
      const isPdf = field.subtype === 'pdf'

      // Determine file type and related validation parameters
      const fileType = isImage ? 'image' : isPdf ? 'pdf' : null
      const maxFiles = field.max || Infinity

      if (!fileType) {
        return // Exit if the subtype or validTypePrefix is not provided
      }

      // Check if the number of uploaded files exceeds the limit
      if (uploadedFiles.length > maxFiles) {
        const message = this.$t('base.maximum_uploaded_files_error_message').replace(':max', maxFiles)
        this.$store.dispatch('base/notifications/push', message)
        return
      }

      // Process each uploaded file
      forEach(uploadedFiles, file => {
        if (fileType === 'image') {
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = e => {
            this.modifiedFormData[fieldKey].push({
              data: e.target.result,
              file,
              name: file?.name || ''
            })
          }
        }
        else if (fileType === 'pdf') {
          this.modifiedFormData[fieldKey].push({
            data: file?.name || null,
            file,
            name: file?.name || ''
          })
        }
      })
    },

    getContentImageSrc (field) {
      let src
      const { key } = field || {}

      if (this.modifiedFormData && key && this.modifiedFormData[key]) {
        // If signature image is a string, that means that it is existing signature image
        if (typeof this.modifiedFormData[key] === 'string') {
          src = this.modifiedFormData[key]
        }
        // Otherwise, if image is an object that means that user uploaded new signature image
        else if (isObject(this.modifiedFormData[key]) && this.modifiedFormData[key].data) {
          src = this.modifiedFormData[key].data
        }
      }

      return src
    },

    getContentWrapperStyle (field) {
      const style = {}
      const { contentStack } = field || {}

      if (!contentStack || contentStack === 'vertical') {
        style.display = 'flex'
        style.flexDirection = 'column'
      }
      else {
        style.display = 'flex'
        style.flexDirection = 'row'
      }

      return style
    },

    getAutocompleteItems (field) {
      const { items, key, hasMore = false } = field || null
      let newItems = {}

      if (!key) {
        return null
      }

      // An array the field declared is the whole truth about what it offers, empty
      // included: a field whose list has just been narrowed down to nothing must draw no
      // options at all, not keep the ones it happened to be showing before. Only a field
      // that declares no list of its own falls back to what the record brought with it.
      if (isArray(items)) {
        newItems[key] = {}
        newItems[key].data = items
        newItems[key].hasMore = !!hasMore
      }
      else if (this.initialAutocompletes && isObject(this.initialAutocompletes)) {
        newItems = this.initialAutocompletes
      }

      return newItems
    },

    getDynamicContentStyle (field) {
      let style = {}
      const { contentStyle } = field || {}
      const isFieldDisabled = this.shouldDisableField(field)

      // set disabled background color if field is disabled
      if (isFieldDisabled) {
        style.backgroundColor = '#F4F4F5'
      }
      if (!contentStyle) {
        return style
      }

      if (isObject(contentStyle)) {
        style = {
          ...style,
          ...contentStyle
        }
      }

      return style
    },

    getContentButtonsWrapperStyle (field) {
      const style = {}
      const { buttonsStack } = field || {}

      if (!buttonsStack || buttonsStack === 'vertical') {
        style.display = 'flex'
        style.flexDirection = 'column'
      }
      else {
        style.display = 'flex'
        style.flexDirection = 'row'
      }

      return style
    },

    getContentButtonStyle (button) {
      let customStyle = {}
      const { style } = button || {}

      if (!style) {
        return customStyle
      }

      if (isObject(style)) {
        customStyle = {
          ...customStyle,
          ...style
        }
      }

      return customStyle
    },

    getContentCardButtonVisibility (button) {
      const { visible } = button || {}
      let isVisible = visible

      if (isVisible === undefined) {
        return true
      }

      if (visible && typeof visible === 'function') {
        isVisible = visible(this)
      }

      return !!isVisible
    },

    getContentButtonClasses (button) {
      let classes = ''
      const { buttonsStack = 'vertical' } = button || {}

      if (buttonsStack === 'vertical') {
        classes += 'mb-2'
      }
      else {
        classes += 'mr-2'
      }

      return classes
    },

    getContentButtonPrependIcon (button) {
      const { prependIcon } = button || {}
      let prependIconValue = ''

      if (!prependIcon) {
        return ''
      }

      if (typeof prependIcon === 'function') {
        prependIconValue = prependIcon(this)
      }
      else if (typeof prependIcon === 'string') {
        prependIconValue = prependIcon
      }

      return prependIconValue
    },

    getContentButtonLabel (button) {
      if (!button?.label) {
        return ''
      }

      const labelValue = typeof button.label === 'function'
        ? button.label(this)
        : button.label

      return typeof labelValue === 'string' ? labelValue : ''
    },

    onContentButtonClick (button) {
      const { click } = button || {}

      if (!click) {
        return
      }

      if (typeof click === 'function') {
        click({
          instance: this,
          formData: this.modifiedFormData,
          indexPage: this.$parent?.$parent
        })
      }
    },

    getImageFieldSrc (field) {
      const { basePath, key, alt } = field || {}

      if (!basePath || !key || typeof basePath !== 'string' || typeof key !== 'string') {
        return null
      }
      return this.modifiedFormData[key] ? basePath + this.modifiedFormData[key] : alt || undefined
    }
  }
}
</script>

<style scoped lang="scss">
.files-upload-div {
  border: 1px solid #E5E7EB;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
}

.label {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #111827;
  line-height: 20px;
  font-weight: 500;
}

.color-field-swatch {
  width: 55px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  cursor: pointer;
}

/* The same label the field components draw above their own inputs, for the field
   types rendered here by hand. */
.field-label {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #111827;
  line-height: 20px;
  font-weight: 500;
  margin-bottom: 10px;
}

.image-label {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #111827;
  line-height: 20px;
  font-weight: 500;
  margin-bottom: 10px;
}
</style>
